// ZUSTAND→ATOM off-ramp (docs/projects/complete/web/zustand-to-effect-atom-migration.md)
// Status: converted
// keepAlive atom + selector hook; per-word match hooks subscribe through
// derived atoms so a match change doesn't rerender every word.
import { useAtomSet, useAtomValue } from '@effect/atom-react';
import { Atom } from 'effect/reactivity';
import type { Word } from '@listening/api-contract/legacy-client-types';
import { docPackWordSearchKey } from '@listening/frontend/doc-pack/search';
import { useCallback, useMemo } from 'react';

type LegacySearchMatch = {
  type?: 'legacy';
  chapterIdx: number;
  paragraphIdx: number;
  sentenceIdx: number;
  wordIdx: number;
  word: Word;
  scrollableIdx: number;
};

type DocPackSearchMatch = {
  type: 'doc-pack';
  page: number;
  charStart: number;
  charEnd: number;
  scrollableIdx: number;
};

export type SearchMatch = LegacySearchMatch | DocPackSearchMatch;

type DocSearchState = {
  query: string;
  matches: Array<SearchMatch>;
  currentMatchIndex: number;
  loadedChapterCount: number;
  totalChapterCount: number;
  _allMatchKeys: Set<string>;
  _currentMatchKey: string | null;
};

type DocSearchActions = {
  setQuery: (query: string) => void;
  setMatches: (
    matches: Array<SearchMatch>,
    coverage: { loaded: number; total: number },
  ) => void;
  nextMatch: () => void;
  prevMatch: () => void;
  clearSearch: () => void;
};

const wordKey = (word: { chunkId: string; wordIdxInChunk: number }) =>
  `${word.chunkId}:${word.wordIdxInChunk.toString()}`;

const searchMatchKey = (match: SearchMatch) =>
  match.type === 'doc-pack'
    ? docPackWordSearchKey({
        page: match.page,
        chars: [match.charStart, match.charEnd],
      })
    : wordKey(match.word);

const clearedSearchState: DocSearchState = {
  query: '',
  matches: [],
  currentMatchIndex: 0,
  loadedChapterCount: 0,
  totalChapterCount: 0,
  _allMatchKeys: new Set(),
  _currentMatchKey: null,
};

const docSearchStateAtom = Atom.make(clearedSearchState).pipe(Atom.keepAlive);

export function useDocSearchStore<T>(
  selector: (state: DocSearchState & DocSearchActions) => T,
): T {
  const setState = useAtomSet(docSearchStateAtom);
  const actions = useMemo<DocSearchActions>(
    () => ({
      setQuery: (query) => {
        setState((current) =>
          query.trim() ? { ...current, query } : clearedSearchState,
        );
      },
      setMatches: (matches, coverage) => {
        const keys = new Set(matches.map(searchMatchKey));
        const currentMatch = matches[0];
        setState((current) => ({
          ...current,
          matches,
          currentMatchIndex: 0,
          loadedChapterCount: coverage.loaded,
          totalChapterCount: coverage.total,
          _allMatchKeys: keys,
          _currentMatchKey: currentMatch ? searchMatchKey(currentMatch) : null,
        }));
      },
      nextMatch: () => {
        setState((current) => {
          if (current.matches.length === 0) return current;
          const next = (current.currentMatchIndex + 1) % current.matches.length;
          const match = current.matches[next];
          return {
            ...current,
            currentMatchIndex: next,
            _currentMatchKey: match ? searchMatchKey(match) : null,
          };
        });
      },
      prevMatch: () => {
        setState((current) => {
          if (current.matches.length === 0) return current;
          const prev =
            (current.currentMatchIndex - 1 + current.matches.length) %
            current.matches.length;
          const match = current.matches[prev];
          return {
            ...current,
            currentMatchIndex: prev,
            _currentMatchKey: match ? searchMatchKey(match) : null,
          };
        });
      },
      clearSearch: () => {
        setState(clearedSearchState);
      },
    }),
    [setState],
  );
  // Selector routed through a derived atom: gated on the selected value
  // (zustand parity), so the reader page root's `query.trim().length > 0`
  // doesn't rerender per keystroke/match-navigation, and selecting an action
  // never rerenders. Same pattern as useIsSearchKeyMatch below.
  return useAtomValue(docSearchStateAtom, (state) =>
    selector({ ...state, ...actions }),
  );
}

export function useIsSearchMatch(word: {
  chunkId: string;
  wordIdxInChunk: number;
}): 'current' | 'other' | false {
  const key = wordKey(word);
  return useIsSearchKeyMatch(key);
}

export function useIsDocPackSearchMatch(word: {
  page: number;
  chars: readonly [number, number];
}): 'current' | 'other' | false {
  return useIsSearchKeyMatch(docPackWordSearchKey(word));
}

// Derived-atom subscription per word: only re-emits when THIS word's status
// changes, so match navigation doesn't rerender every rendered word.
function useIsSearchKeyMatch(key: string): 'current' | 'other' | false {
  const selectStatus = useCallback(
    (state: DocSearchState): 'current' | 'other' | false => {
      if (state._currentMatchKey === key) return 'current';
      if (state._allMatchKeys.has(key)) return 'other';
      return false;
    },
    [key],
  );
  return useAtomValue(docSearchStateAtom, selectStatus);
}
